import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { propertyTypes, frequencies, serviceLevels, addOns } from '../../mocks/services';

export default function QuotePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    propertyType: '',
    bedrooms: 2,
    bathrooms: 1,
    frequency: 'one-off',
    serviceLevel: 'standard',
    addOns: [] as string[],
    postcode: '',
    date: '',
    time: ''
  });

  const calculatePrice = () => {
    const baseRate = 20;
    const serviceLevel = serviceLevels.find(s => s.id === formData.serviceLevel);
    const multiplier = serviceLevel?.multiplier || 1;
    
    let hours = 2;
    if (formData.propertyType === 'house') hours += formData.bedrooms * 0.5;
    if (formData.propertyType === 'flat') hours += formData.bedrooms * 0.3;
    if (formData.propertyType === 'office') hours += 1;
    
    hours += formData.bathrooms * 0.5;
    
    let basePrice = baseRate * hours * multiplier;
    
    let addOnPrice = 0;
    formData.addOns.forEach(addonId => {
      const addon = addOns.find(a => a.id === addonId);
      if (addon) addOnPrice += addon.price;
    });
    
    return {
      total: basePrice + addOnPrice,
      hours: hours.toFixed(1),
      basePrice,
      addOnPrice
    };
  };

  const handleSubmit = () => {
    const quote = calculatePrice();
    navigate('/quote-result', { state: { formData, quote } });
  };

  const toggleAddOn = (addonId: string) => {
    setFormData(prev => ({
      ...prev,
      addOns: prev.addOns.includes(addonId)
        ? prev.addOns.filter(id => id !== addonId)
        : [...prev.addOns, addonId]
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/5676cecc8bee6a08ffa3da23e35cdc4e/0d23c322700fa567c5c819043b938654.png" 
                alt="I-kleanGO" 
                className="h-[95px] md:h-[130px] w-auto"
              />
            </Link>
            <Link to="/" className="text-slate-700 hover:text-teal-600 transition-colors text-sm font-medium cursor-pointer">
              ← Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-48 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex items-center justify-between">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center flex-1">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= s ? 'bg-teal-600 text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step > s ? '✓' : s}
                  </div>
                  {s < 4 && (
                    <div className={`flex-1 h-1 mx-2 ${
                      step > s ? 'bg-teal-600' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-3 text-xs text-slate-600">
              <span>Property</span>
              <span>Details</span>
              <span>Service</span>
              <span>Booking</span>
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            {/* Step 1: Property Type */}
            {step === 1 && (
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-8">What type of property?</h2>
                <div className="grid grid-cols-3 gap-6 mb-8">
                  {propertyTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setFormData({ ...formData, propertyType: type.id })}
                      className={`p-8 rounded-xl border-2 transition-all cursor-pointer ${
                        formData.propertyType === type.id
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      <i className={`${type.icon} text-4xl text-teal-600 mb-4`}></i>
                      <div className="font-semibold text-slate-900">{type.name}</div>
                    </button>
                  ))}
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-4 mt-8">Property Size</h3>
                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Bedrooms</label>
                    <select
                      value={formData.bedrooms}
                      onChange={(e) => setFormData({ ...formData, bedrooms: Number(e.target.value) })}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer"
                    >
                      {[0, 1, 2, 3, 4, 5].map(n => (
                        <option key={n} value={n}>{n === 5 ? '5+' : n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Bathrooms</label>
                    <select
                      value={formData.bathrooms}
                      onChange={(e) => setFormData({ ...formData, bathrooms: Number(e.target.value) })}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer"
                    >
                      {[1, 2, 3, 4].map(n => (
                        <option key={n} value={n}>{n === 4 ? '4+' : n}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Frequency */}
            {step === 2 && (
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-8">How often do you need cleaning?</h2>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  {frequencies.map((freq) => (
                    <button
                      key={freq.id}
                      onClick={() => setFormData({ ...formData, frequency: freq.id })}
                      className={`p-6 rounded-xl border-2 transition-all font-semibold cursor-pointer ${
                        formData.frequency === freq.id
                          ? 'border-teal-600 bg-teal-50 text-teal-700'
                          : 'border-slate-200 text-slate-700 hover:border-teal-300'
                      }`}
                    >
                      {freq.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Service Level & Add-ons */}
            {step === 3 && (
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-8">Choose your service level</h2>
                <div className="grid grid-cols-2 gap-6 mb-12">
                  {serviceLevels.map((level) => (
                    <button
                      key={level.id}
                      onClick={() => setFormData({ ...formData, serviceLevel: level.id })}
                      className={`p-6 rounded-xl border-2 transition-all text-left cursor-pointer relative ${
                        formData.serviceLevel === level.id
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      {level.popular && (
                        <div className="absolute -top-3 right-4 bg-gradient-to-r from-amber-500 to-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full">
                          Most Popular
                        </div>
                      )}
                      <div className="text-xs font-bold text-teal-600 mb-2">{level.badge}</div>
                      <div className="font-bold text-slate-900 mb-1">{level.name}</div>
                      <div className="text-sm text-slate-600 mb-4">{level.description}</div>
                      <ul className="space-y-2">
                        {level.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                            <i className="ri-checkbox-circle-fill text-teal-600 mt-0.5"></i>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-6">Optional Add-ons</h3>
                <div className="grid grid-cols-3 gap-4">
                  {addOns.map((addon) => (
                    <button
                      key={addon.id}
                      onClick={() => toggleAddOn(addon.id)}
                      className={`p-4 rounded-xl border-2 transition-all cursor-pointer relative ${
                        formData.addOns.includes(addon.id)
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-slate-200 hover:border-teal-300'
                      }`}
                    >
                      {formData.addOns.includes(addon.id) && (
                        <i className="ri-checkbox-circle-fill text-teal-600 absolute top-2 right-2"></i>
                      )}
                      <i className={`${addon.icon} text-2xl text-teal-600 mb-2`}></i>
                      <div className="font-semibold text-slate-900 text-sm mb-1">{addon.name}</div>
                      <div className="text-teal-600 font-bold">+£{addon.price}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 4: Location & Date */}
            {step === 4 && (
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-8">When and where?</h2>
                
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
                  <div className="flex items-start gap-3">
                    <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                    <div className="text-sm text-blue-900">
                      <strong>Pilot Area:</strong> Currently serving Oxford & Oxfordshire only
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Postcode</label>
                    <input
                      type="text"
                      placeholder="OX1 2JD"
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Date</label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Preferred Time</label>
                      <select
                        value={formData.time}
                        onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                        className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer"
                      >
                        <option value="">Select time</option>
                        <option value="08:00">08:00 AM</option>
                        <option value="09:00">09:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="13:00">01:00 PM</option>
                        <option value="14:00">02:00 PM</option>
                        <option value="15:00">03:00 PM</option>
                        <option value="16:00">04:00 PM</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-8 border-t border-slate-200">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-8 py-3 rounded-full border-2 border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  ← Back
                </button>
              )}
              {step < 4 ? (
                <button
                  onClick={() => setStep(step + 1)}
                  disabled={step === 1 && !formData.propertyType}
                  className="ml-auto px-8 py-3 rounded-full bg-gradient-to-r from-teal-600 to-teal-500 text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  Continue →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!formData.postcode || !formData.date || !formData.time}
                  className="ml-auto px-8 py-3 rounded-full bg-gradient-to-r from-teal-600 to-teal-500 text-white font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  Get My Quote →
                </button>
              )}
            </div>
          </div>

          {/* Live Price Preview */}
          <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl border-t border-slate-200 py-4 px-6">
            <div className="max-w-4xl mx-auto flex items-center justify-between">
              <div>
                <div className="text-sm text-slate-600">Estimated Total</div>
                <div className="text-3xl font-bold text-slate-900">
                  £{calculatePrice().total.toFixed(2)}
                </div>
              </div>
              <div className="text-sm text-slate-600">
                Estimated Duration: {calculatePrice().hours} hours
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}